import React from 'react';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { NumberField } from '@/components/ui/number-field';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Calendar, FileText, FolderKanban, ShieldCheck, CalendarClock, Sparkles, MessageSquare } from 'lucide-react';
import { getDayName } from '@/types/timesheetSettings';
import type { WorkingProfile } from '@/types/timesheetSettings';

// Maps day index to WorkingProfile day key
const DAY_INDEX_TO_KEY: Record<number, keyof WorkingProfile['workingDays']> = {
  0: 'sunday',
  1: 'monday',
  2: 'tuesday',
  3: 'wednesday',
  4: 'thursday',
  5: 'friday',
  6: 'saturday',
};

// The Project Selection Scope setting is hidden until its status validation is confirmed
const SHOW_PROJECT_SELECTION_SCOPE = false;

const DAY_LABELS: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday (Recommended)',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};

interface TimeEntryRulesAccordionProps {
  // Week Configuration
  weekStartDay: 0 | 1 | 2 | 3 | 4 | 5 | 6;
  onWeekStartDayChange: (day: 0 | 1 | 2 | 3 | 4 | 5 | 6) => void;
  workingProfile?: WorkingProfile | null;

  // Required Fields
  showRole: boolean;
  requireRole: boolean;
  showCostCentre: boolean;
  requireCostCentre: boolean;
  requireDescription: 'never' | 'always';
  descriptionType: 'row' | 'daily';
  onShowRoleChange: (show: boolean) => void;
  onRequireRoleChange: (require: boolean) => void;
  onShowCostCentreChange: (show: boolean) => void;
  onRequireCostCentreChange: (require: boolean) => void;
  onRequireDescriptionChange: (value: 'never' | 'always') => void;
  onDescriptionTypeChange: (value: 'row' | 'daily') => void;

  // Project Selection
  projectSelectionScope: 'all' | 'assigned';
  showArchivedProjects: boolean;
  showProjectTimeRemaining: boolean;
  onProjectSelectionScopeChange: (scope: 'all' | 'assigned') => void;
  onShowArchivedProjectsChange: (show: boolean) => void;
  onShowProjectTimeRemainingChange: (show: boolean) => void;

  // Validation Rules
  minimumEntryDuration: '6min' | '15min' | '30min' | '1hour';
  timeRounding: 'none' | '15min' | '30min' | '1hour';
  allowNegativeHours: boolean;
  onMinimumEntryDurationChange: (duration: '6min' | '15min' | '30min' | '1hour') => void;
  onTimeRoundingChange: (rounding: 'none' | '15min' | '30min' | '1hour') => void;
  onAllowNegativeHoursChange: (allow: boolean) => void;

  // Date Restrictions
  historicalEntryLimit: number;
  allowFutureDatedEntries: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited';
  onHistoricalEntryLimitChange: (weeks: number) => void;
  onAllowFutureDatedEntriesChange: (allow: 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited') => void;

  // Smart Features
  enableCopyPreviousWeek: boolean;
  copyDescriptions: boolean;
  copyOnlyBillable: boolean;
  enableAutoSmartFill: boolean;
  enableAiInsights: boolean;
  onEnableCopyPreviousWeekChange: (enable: boolean) => void;
  onCopyDescriptionsChange: (copy: boolean) => void;
  onCopyOnlyBillableChange: (copy: boolean) => void;
  onEnableAutoSmartFillChange: (enable: boolean) => void;
  onEnableAiInsightsChange: (enable: boolean) => void;
}

export function TimeEntryRulesAccordion(props: TimeEntryRulesAccordionProps) {
  return (
    <Accordion type="multiple" defaultValue={["basic", "validation", "restrictions", "smart"]} className="space-y-4">

      {/* ── Basic Settings ─────────────────────────────────────────────── */}
      <AccordionItem value="basic" className="border rounded-lg">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Calendar className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Basic Settings</div>
              <div className="text-sm text-muted-foreground font-normal">
                Week configuration, required fields, and project selection
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pt-2 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

            {/* ── Week Configuration ── */}
            <div className="flex items-center gap-2 px-1 pt-1 pb-0.5 lg:col-span-2">
              <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Week Configuration</span>
            </div>

            {/* Week Start Day */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="weekStartDay" className="text-sm font-medium">Week Start Day</Label>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Determines the starting day for timesheet weeks, weekly totals, and deadline calculations.
                  Currently set to <strong>{getDayName(props.weekStartDay)}</strong>.
                </p>
              </div>
              <Select
                value={props.weekStartDay.toString()}
                onValueChange={(value) => props.onWeekStartDayChange(parseInt(value) as 0 | 1 | 2 | 3 | 4 | 5 | 6)}
              >
                <SelectTrigger id="weekStartDay" className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {([0, 1, 2, 3, 4, 5, 6] as const).filter((dayIdx) => {
                    if (!props.workingProfile) return true;
                    const key = DAY_INDEX_TO_KEY[dayIdx];
                    return props.workingProfile.workingDays[key].isWorkingDay;
                  }).map((dayIdx) => (
                    <SelectItem key={dayIdx} value={dayIdx.toString()}>
                      {DAY_LABELS[dayIdx]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* ── Required Fields ── */}
            <div className="flex items-center gap-2 px-1 pt-2 pb-0.5 lg:col-span-2">
              <FileText className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Required Fields</span>
            </div>

            {/* Show Role */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="showRole" className="text-sm font-medium cursor-pointer">Show Role Field</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Display role selector on timesheet entries</p>
              </div>
              <Checkbox
                id="showRole"
                checked={props.showRole}
                onCheckedChange={(v) => props.onShowRoleChange(!!v)}
              />
            </div>

            {/* Require Role */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label
                  htmlFor="requireRole"
                  className={`text-sm font-medium cursor-pointer ${!props.showRole ? 'text-muted-foreground' : ''}`}
                >
                  Require Role
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">Make role selection mandatory (only if shown)</p>
              </div>
              <Checkbox
                id="requireRole"
                checked={props.requireRole}
                disabled={!props.showRole}
                onCheckedChange={(v) => props.onRequireRoleChange(!!v)}
              />
            </div>

            {/* Show Cost Centre */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="showCostCentre" className="text-sm font-medium cursor-pointer">Show Cost Centre Field</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Display cost centre selector on timesheet entries</p>
              </div>
              <Checkbox
                id="showCostCentre"
                checked={props.showCostCentre}
                onCheckedChange={(v) => props.onShowCostCentreChange(!!v)}
              />
            </div>

            {/* Require Cost Centre */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label
                  htmlFor="requireCostCentre"
                  className={`text-sm font-medium cursor-pointer ${!props.showCostCentre ? 'text-muted-foreground' : ''}`}
                >
                  Require Cost Centre
                </Label>
                <p className="text-xs text-muted-foreground mt-0.5">Make cost centre selection mandatory (only if shown)</p>
              </div>
              <Checkbox
                id="requireCostCentre"
                checked={props.requireCostCentre}
                disabled={!props.showCostCentre}
                onCheckedChange={(v) => props.onRequireCostCentreChange(!!v)}
              />
            </div>

            {/* ── Descriptions ── */}
            <div className="flex items-center gap-2 px-1 pt-2 pb-0.5 lg:col-span-2">
              <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Descriptions</span>
            </div>

            {/* Description Type */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="descriptionType" className="text-sm font-medium">Description Type</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Per Row shows a single description field for the full week. Daily shows a note field on each day that has hours logged.</p>
              </div>
              <Select
                value={props.descriptionType}
                onValueChange={(v) => props.onDescriptionTypeChange(v as 'row' | 'daily')}
              >
                <SelectTrigger id="descriptionType" className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="row">Per Row</SelectItem>
                  <SelectItem value="daily">Daily</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Require Description */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="requireDescription" className="text-sm font-medium">Require Description</Label>
                <p className="text-xs text-muted-foreground mt-0.5">When must entries include a description</p>
              </div>
              <Select
                value={props.requireDescription}
                onValueChange={props.onRequireDescriptionChange}
              >
                <SelectTrigger id="requireDescription" className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="never">Never required</SelectItem>
                  <SelectItem value="always">Always required</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* ── Project Selection ── */}
            <div className="flex items-center gap-2 px-1 pt-2 pb-0.5 lg:col-span-2">
              <FolderKanban className="h-3.5 w-3.5 text-muted-foreground" />
              <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Project Selection</span>
            </div>

            {/* Project Selection Scope */}
            {SHOW_PROJECT_SELECTION_SCOPE && (
              <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
                <div className="flex-1 min-w-0">
                  <Label htmlFor="projectSelectionScope" className="text-sm font-medium">Project Selection Scope</Label>
                  <p className="text-xs text-muted-foreground mt-0.5">Which projects can employees choose from</p>
                </div>
                <Select
                  value={props.projectSelectionScope}
                  onValueChange={(value) => props.onProjectSelectionScopeChange(value as 'all' | 'assigned')}
                >
                  <SelectTrigger id="projectSelectionScope" className="w-[160px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All active projects</SelectItem>
                    <SelectItem value="assigned">Assigned projects only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Show Archived Projects */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="showArchivedProjects" className="text-sm font-medium cursor-pointer">Show Archived Projects</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Include archived/closed projects in selection list</p>
              </div>
              <Checkbox
                id="showArchivedProjects"
                checked={props.showArchivedProjects}
                onCheckedChange={(v) => props.onShowArchivedProjectsChange(!!v)}
              />
            </div>

            {/* Show Project Time Remaining */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="showProjectTimeRemaining" className="text-sm font-medium cursor-pointer">Show Project Time Remaining</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Display budget remaining when selecting projects</p>
              </div>
              <Checkbox
                id="showProjectTimeRemaining"
                checked={props.showProjectTimeRemaining}
                onCheckedChange={(v) => props.onShowProjectTimeRemainingChange(!!v)}
              />
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>

      {/* ── Validation Rules ───────────────────────────────────────────── */}
      <AccordionItem value="validation" className="border rounded-lg">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <ShieldCheck className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Validation Rules</div>
              <div className="text-sm text-muted-foreground font-normal">
                Time entry formatting and validation constraints
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pt-2 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

            {/* Minimum Entry Duration */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="minimumEntryDuration" className="text-sm font-medium">Minimum Entry Duration</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Smallest time increment that can be logged</p>
              </div>
              <Select
                value={props.minimumEntryDuration}
                onValueChange={props.onMinimumEntryDurationChange}
              >
                <SelectTrigger id="minimumEntryDuration" className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6min">6 minutes (0.1 hours)</SelectItem>
                  <SelectItem value="15min">15 minutes (0.25 hours)</SelectItem>
                  <SelectItem value="30min">30 minutes (0.5 hours)</SelectItem>
                  <SelectItem value="1hour">1 hour</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Time Rounding */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="timeRounding" className="text-sm font-medium">Time Rounding</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Automatically round time entries to nearest increment</p>
              </div>
              <Select
                value={props.timeRounding}
                onValueChange={props.onTimeRoundingChange}
              >
                <SelectTrigger id="timeRounding" className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">None (no rounding)</SelectItem>
                  <SelectItem value="15min">15 minutes</SelectItem>
                  <SelectItem value="30min">30 minutes</SelectItem>
                  <SelectItem value="1hour">1 hour</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Allow Negative Hours */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="allowNegativeHours" className="text-sm font-medium cursor-pointer">Allow Negative Hours</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Permit negative values for corrections/adjustments</p>
              </div>
              <Checkbox
                id="allowNegativeHours"
                checked={props.allowNegativeHours}
                onCheckedChange={(v) => props.onAllowNegativeHoursChange(!!v)}
              />
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>

      {/* ── Date Restrictions ──────────────────────────────────────────── */}
      <AccordionItem value="restrictions" className="border rounded-lg">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <CalendarClock className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Date Restrictions</div>
              <div className="text-sm text-muted-foreground font-normal">
                Control how far back or forward time can be logged
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pt-2 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

            {/* Historical Entry Limit */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="historicalEntryLimit" className="text-sm font-medium">Historical Entry Limit</Label>
                <p className="text-xs text-muted-foreground mt-0.5">How many weeks back can time be entered</p>
              </div>
              <div className="flex items-center gap-2">
                <NumberField
                  id="historicalEntryLimit"
                  min={1}
                  max={52}
                  value={props.historicalEntryLimit}
                  onChange={props.onHistoricalEntryLimitChange}
                  className="w-20 text-center"
                />
                <span className="text-sm text-muted-foreground">weeks back</span>
              </div>
            </div>

            {/* Allow Future-Dated Entries */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="allowFutureDatedEntries" className="text-sm font-medium">Allow Future-Dated Entries</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Can employees log time for future dates</p>
              </div>
              <Select
                value={props.allowFutureDatedEntries}
                onValueChange={props.onAllowFutureDatedEntriesChange}
              >
                <SelectTrigger id="allowFutureDatedEntries" className="w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="no">No (current week only)</SelectItem>
                  <SelectItem value="1week">Yes, up to 1 week ahead</SelectItem>
                  <SelectItem value="2weeks">Yes, up to 2 weeks ahead</SelectItem>
                  <SelectItem value="4weeks">Yes, up to 4 weeks ahead</SelectItem>
                  <SelectItem value="unlimited">Yes, unlimited</SelectItem>
                </SelectContent>
              </Select>
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>

      {/* ── Smart Features ─────────────────────────────────────────────── */}
      <AccordionItem value="smart" className="border rounded-lg">
        <AccordionTrigger className="hover:no-underline">
          <div className="flex items-center gap-3">
            <div className="rounded-lg bg-primary/10 p-2">
              <Sparkles className="h-4 w-4 text-primary" />
            </div>
            <div className="text-left">
              <div className="font-semibold">Smart Features</div>
              <div className="text-sm text-muted-foreground font-normal">
                Intelligent tools to streamline time entry
              </div>
            </div>
          </div>
        </AccordionTrigger>
        <AccordionContent className="pt-2 pb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">

            {/* Enable Copy Previous Week */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="enable-copy-previous-week" className="text-sm font-medium cursor-pointer">Enable 'Copy Previous Week'</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Show button to copy entries from previous week</p>
              </div>
              <Checkbox
                id="enable-copy-previous-week"
                checked={props.enableCopyPreviousWeek}
                onCheckedChange={(v) => props.onEnableCopyPreviousWeekChange(!!v)}
              />
            </div>

            {/* Copy Descriptions */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="copy-descriptions" className="text-sm font-medium cursor-pointer">Copy Descriptions</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Include entry descriptions when copying</p>
              </div>
              <Checkbox
                id="copy-descriptions"
                checked={props.copyDescriptions}
                onCheckedChange={(v) => props.onCopyDescriptionsChange(!!v)}
              />
            </div>

            {/* Copy Only Billable */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="copy-only-billable" className="text-sm font-medium cursor-pointer">Copy Only Billable Entries</Label>
                <p className="text-xs text-muted-foreground mt-0.5">When copying, exclude non-billable time</p>
              </div>
              <Checkbox
                id="copy-only-billable"
                checked={props.copyOnlyBillable}
                onCheckedChange={(v) => props.onCopyOnlyBillableChange(!!v)}
              />
            </div>

            {/* Enable Auto Smart Fill */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="enable-auto-smart-fill" className="text-sm font-medium cursor-pointer">Enable Auto Smart Fill</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Automatically suggest time entries based on patterns</p>
              </div>
              <Checkbox
                id="enable-auto-smart-fill"
                checked={props.enableAutoSmartFill}
                onCheckedChange={(v) => props.onEnableAutoSmartFillChange(!!v)}
              />
            </div>

            {/* Enable AI Insights */}
            <div className="flex items-center justify-between gap-4 p-4 rounded-lg border bg-background">
              <div className="flex-1 min-w-0">
                <Label htmlFor="enable-ai-insights" className="text-sm font-medium cursor-pointer">Enable AI Insights</Label>
                <p className="text-xs text-muted-foreground mt-0.5">Show AI-powered insights and anomaly detection</p>
              </div>
              <Checkbox
                id="enable-ai-insights"
                checked={props.enableAiInsights}
                onCheckedChange={(v) => props.onEnableAiInsightsChange(!!v)}
              />
            </div>

          </div>
        </AccordionContent>
      </AccordionItem>

    </Accordion>
  );
}
